import { _lang, lang } from '@umu/ukit-lang';
import { contactSales } from '@umu/umu-api';
import { IOutputContactSales } from '@umu/umu-api/src/services/contractSales/contractSales';
import { Checkbox, Select } from '@umu/umu-design';
import { CheckboxChangeEvent } from '@umu/umu-design/es/components/Checkbox';
import { $document } from '@umu/umu-dom';
import { localStorageHelper, STORAGE_KEY } from '@umu/umu-storage';
import { useDebounceFn } from 'ahooks';
import { IChangeCallbackArgs } from 'businessCommon';
import { MailInput, PhoneInput } from 'businessCommon/src/components';
import { adapterService, validateMail } from 'businessCommon/src/services';
import { Button, CustomDialog, If, Textarea } from 'common/src/components';
import { handlePromiseError } from 'common/utils/errorHandler';
import React, { useEffect, useMemo, useState } from 'react';
import { isCom, isCoSite, isIo } from 'utils/util';
import { amountOptionList, getDefaultState, jobOptionList } from './dataFlow';
import './index.scss';
import { ChannelType, FormType, IProp, OptionItemType, ParamsType } from './interface';

const scrollIntoView = () => {
    const errorTipNode = document.querySelector('.form-tip.error');
    if (errorTipNode) {
        errorTipNode.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
};

function Form(props: IProp) {
    const [formState, SetFormState] = useState(getDefaultState);
    localStorageHelper.set(STORAGE_KEY.AREA_CODE, '');

    useEffect(() => {
        adapterService.init();
    }, []);

    const lastNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.lastName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const firstNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.firstName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const enterpriseNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.enterpriseName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const mailChangeCallback = (data: string) => {
        SetFormState((pre) => {
            pre.email = data;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const positionNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.positionName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const positionNameSelectChangeCallback = (item: OptionItemType) => {
        const value = item.id;
        SetFormState((pre) => {
            pre.positionName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const phoneChangeCallback = (data: IChangeCallbackArgs) => {
        SetFormState((pre) => {
            pre.codePosition = data.selectedCountryPosition + '';
            pre.areaCode = data.countryCode + '';
            pre.phone = data.phoneNumber + '';
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const descriptionChangeCallback = (value: string) => {
        value = value.trim();
        SetFormState((pre) => {
            pre.description = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const departmentChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.department = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const staffCountChangeCallback = (item: OptionItemType) => {
        const value = item.id;
        SetFormState((pre) => {
            pre.staffCount = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const receiveSubscribeChangeCallback = (e: CheckboxChangeEvent) => {
        const {
            target: { checked },
        } = e;
        SetFormState((pre) => {
            pre.receiveSubscribe = checked;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const checkLastName = (lastName: string) => {
        const result = (lastName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showLastName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };

    const checkFirstName = (firstName: string) => {
        const result = (firstName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showFirstName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };

    const checkMail = (email: string) => {
        const result = validateMail(email);
        SetFormState((pre) => {
            pre.showMailMsg = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkEnterpriseName = (enterpriseName: string) => {
        const result = (enterpriseName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showEnterpriseName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkPositionName = (positionName: string) => {
        const result = (positionName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showPositionName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const resetState = () => {
        const eles = $document.querySelectorAll('input');
        eles.forEach((ele) => {
            if (ele.type === 'text' || ele.type === 'email' || ele.type === 'tel') {
                ele.value = '';
            }
        });
        document.querySelectorAll('textarea').forEach((ele) => {
            ele.value = '';
        });
        SetFormState((pre) => {
            pre = getDefaultState;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };

    const submitFormSuccess = () => {
        switch (props.type) {
            case FormType.contractSales:
                CustomDialog({
                    className: 'cotact-sales-dialog',
                    canClose: true,
                    width: '4rem',
                    childProps: {
                        resetState,
                    },
                    contentComponent: props.contentComponent,
                });
                return;
            case FormType.brandGuide:
            case FormType.whitePaper:
            case FormType.practiceMakesPerformance:
            case FormType.aiEmpowermentOrganizationalTransformation:
            case FormType.aiFutureOfWork:
            case FormType.talentTransformationAgeOfAi:
                if (props.downloadUrl) {
                    window.open(props.downloadUrl, '_blank');
                }
                resetState();
                props.changeHasSubmitted?.(true);
                return;
        }
    };

    const setPropertyOfChannelType = (params: ParamsType) => {
        switch (props.type) {
            case FormType.whitePaper:
            case FormType.brandGuide:
            case FormType.whitePaper:
            case FormType.practiceMakesPerformance:
            case FormType.aiEmpowermentOrganizationalTransformation:
            case FormType.aiFutureOfWork:
            case FormType.talentTransformationAgeOfAi:
                params.channel_type = ChannelType[props.type];
        }
    };

    const submitForm = useDebounceFn(
        () => {
            const {
                lastName,
                firstName,
                phone,
                email,
                enterpriseName,
                positionName,
                areaCode,
                description,
                department,
                staffCount,
                receiveSubscribe,
            } = formState;

            // 手机号选填，不校验
            const hasError =
                !checkLastName(lastName) ||
                !checkFirstName(firstName) ||
                !checkMail(email) ||
                !checkEnterpriseName(enterpriseName) ||
                !checkPositionName(positionName);

            // 仅在触发提交，且存在校验异常时滚动到视口
            if (hasError) {
                // 异步队列中执行滚动，避免首次校验状态还未更新完成，找不对对应的 error element
                Promise.resolve()
                    .then(() => {
                        scrollIntoView();
                    })
                    .catch((error: unknown) => {
                        console.error('Form scrollIntoView error:', error);
                    });
                return;
            }

            const params: any = {
                first_name: firstName,
                last_name: lastName,
                phone: `${areaCode}-${phone}`,
                email,
                enterprise_name: enterpriseName,
                position_name: positionName,
                source: props.referUrl,
                description,
                department,
                staff_count: staffCount,
            };

            if (props.type === FormType.contractSales && (isCom() || isIo())) {
                params.receive_subscribe = Number(receiveSubscribe);
            }

            setPropertyOfChannelType(params);
            contactSales(params)
                .then((res: IOutputContactSales) => {
                    if (res.data) {
                        submitFormSuccess();
                    }
                })
                .catch((error: unknown) => {
                    handlePromiseError(error, 'Form.contactSales', 'Form contactSales error', {
                        ...params,
                        formType: props.type,
                    });
                });
            adapterService.track(params, props.type);
        },
        { wait: 3000, leading: true, trailing: false },
    );

    const isEn = useMemo(() => {
        return _lang('en');
    }, []);

    const getPlaceHolder = (ph?: string) => {
        if (isEn) {
            return props.platform === 'twww' ? '' : ph;
        }
        return ph;
    };

    const getPlaceHolderByPlatform = (ph?: string, mph?: string) => {
        return props.platform === 'twww' ? ph : mph;
    };

    const lastNameElement = (
        <div
            key={'lastNameElement'}
            className={formState.showLastName ? 'input name last-name error' : 'input name last-name'}
        >
            <If condition={props.platform === 'twww'}>
                <div className="lable-title">
                    {lang('姓')}
                    <i className="icon icon-required" />
                </div>
            </If>
            <input
                type="text"
                placeholder={getPlaceHolder(lang('请填写您的姓'))}
                defaultValue={formState.lastName}
                onChange={lastNameChangeCallback}
            />

            <If condition={formState.showLastName}>
                <p className="form-tip error">{lang('请填写您的姓')}</p>
            </If>
        </div>
    );

    const firstNameElement = (
        <div key={'firstNameElement'} className={formState.showFirstName ? 'input name error' : 'input name'}>
            <If condition={props.platform === 'twww'}>
                <div className="lable-title">
                    {lang('名')}
                    <i className="icon icon-required" />
                </div>
            </If>
            <input
                type="text"
                defaultValue={formState.firstName}
                onChange={firstNameChangeCallback}
                placeholder={getPlaceHolder(lang('请填写您的名'))}
            />

            <If condition={formState.showFirstName}>
                <p className="form-tip error">{lang('请填写您的名')}</p>
            </If>
        </div>
    );

    const getUserNameForms = () => {
        if (isEn) {
            return [firstNameElement, lastNameElement];
        }

        return [lastNameElement, firstNameElement];
    };

    const renderButton = () => {
        switch (props.type) {
            case FormType.contractSales:
                return (
                    <Button className="contract-sales-btn" onClick={() => submitForm.run()}>
                        {isEn ? 'Submit' : lang('联系 UMU 销售')}
                    </Button>
                );

            case FormType.brandGuide:
                return (
                    <Button className="white-paper-btn brandGuide" onClick={() => submitForm.run()}>
                        {lang('下载 UMU 品牌素材')}
                    </Button>
                );

            case FormType.whitePaper:
                return (
                    <Button className="white-paper-btn" onClick={() => submitForm.run()}>
                        {lang('马上获取')}
                    </Button>
                );

            case FormType.tappingThePotentialOfSE:
                if (props.platform === 'twww') {
                    return <Button className="tapping-the-potential-of-se twww">Download</Button>;
                }
                return <Button className="tapping-the-potential-of-se mobile">Submit</Button>;
            case FormType.practiceMakesPerformance:
                return (
                    <Button
                        className={`practice-makes-performance-btn ${props.platform === 'mobile' && 'mobile'}`}
                        onClick={() => submitForm.run()}
                    >
                        {isEn ? (props.platform === 'mobile' ? 'Submit' : 'Download') : lang('马上获取')}
                    </Button>
                );
            case FormType.aiEmpowermentOrganizationalTransformation:
                return (
                    <Button
                        className={`ai-empowerment-organizational-transformation-btn ${
                            props.platform === 'mobile' && 'mobile'
                        }`}
                        onClick={() => submitForm.run()}
                    >
                        {lang('Download')}
                    </Button>
                );
            case FormType.aiFutureOfWork:
                return (
                    <Button
                        className={`ai-future-of-work-btn ${props.platform === 'mobile' && 'mobile'}`}
                        onClick={() => submitForm.run()}
                    >
                        {lang('Download')}
                    </Button>
                );
            case FormType.talentTransformationAgeOfAi:
                return (
                    <Button
                        className={`talent-transformation-age-of-ai-btn ${props.platform === 'mobile' && 'mobile'}`}
                        onClick={() => submitForm.run()}
                    >
                        {lang('Download')}
                    </Button>
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        document.body.classList.add('form-body');
        return () => {
            document.body.classList.remove('form-body');
        };
    }, []);

    return (
        <>
            <div className="form-row form-user-row">{getUserNameForms()}</div>
            <div className="form-row">
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">{isEn ? 'Phone number' : lang('手机号码')}</div>
                </If>
                <PhoneInput
                    initialPhone={formState.phone}
                    initialValue={formState.areaCode}
                    initialPosition={formState.codePosition}
                    changeCallback={phoneChangeCallback}
                    placeholder={isEn ? getPlaceHolderByPlatform('Optional', 'Phone (optional)') : getPlaceHolder()}
                />
            </div>
            <div className={formState.showMailMsg ? 'form-row mail-input-wrap error' : 'form-row mail-input-wrap'}>
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">
                        {isEn ? 'Work email' : lang('电子邮箱')}
                        <i className="icon icon-required" />
                    </div>
                </If>
                <MailInput
                    initialVal={formState.email}
                    isShowSuggestion={false}
                    changeCallback={mailChangeCallback}
                    placeholder={getPlaceHolder('name@company.com')}
                />

                <If condition={formState.showMailMsg}>
                    <p className="form-tip error">{lang('请输入有效的电子邮箱')}</p>
                </If>
            </div>
            <div className={formState.showEnterpriseName ? 'form-row error' : 'form-row'}>
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">
                        {isEn ? 'Company name' : lang('企业或单位名称')}
                        <i className="icon icon-required" />
                    </div>
                </If>
                <div className="input">
                    <input
                        type="text"
                        defaultValue={formState.enterpriseName}
                        onChange={enterpriseNameChangeCallback}
                        placeholder={getPlaceHolder(lang('请填写企业或单位名称'))}
                    />
                </div>
                <If condition={formState.showEnterpriseName}>
                    <p className="form-tip error">{lang('请填写企业或单位名称')}</p>
                </If>
            </div>
            <If condition={isCoSite()}>
                <div className="form-row">
                    <If condition={props.platform === 'twww'}>
                        <div className="lable-title">{_lang('jp') ? '部署' : lang('部门')}</div>
                    </If>
                    <div className="input">
                        <input
                            type="text"
                            defaultValue={formState.department}
                            onChange={departmentChangeCallback}
                            placeholder={getPlaceHolder(
                                _lang('jp') ? '部署名を入力してください' : lang('请填写部门名称'),
                            )}
                        />
                    </div>
                </div>
            </If>
            <div className={`${formState.showPositionName ? 'form-row error' : 'form-row'}`}>
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">
                        {lang('职位')}
                        <i className="icon icon-required" />
                    </div>
                </If>
                <If condition={!isCoSite()}>
                    <div className="input">
                        <input
                            type="text"
                            defaultValue={formState.positionName}
                            onChange={positionNameChangeCallback}
                            placeholder={getPlaceHolder(lang('请填写您的职位'))}
                        />
                    </div>
                </If>
                <If condition={isCoSite()}>
                    <div className="select">
                        <Select
                            selected={formState.positionName}
                            list={jobOptionList}
                            placeholder={getPlaceHolder(lang('请选择您的职位'))}
                            onSelect={positionNameSelectChangeCallback}
                        />
                    </div>
                </If>
                <If condition={formState.showPositionName}>
                    {isCoSite() ? (
                        <p className="form-tip error">{getPlaceHolder(lang('请选择您的职位'))}</p>
                    ) : (
                        <p className="form-tip error">{getPlaceHolder(lang('请填写您的职位'))}</p>
                    )}
                </If>
            </div>
            <If condition={isCoSite()}>
                <div className="form-row">
                    <If condition={props.platform === 'twww'}>
                        <div className="lable-title">{lang('员工人数')}</div>
                    </If>
                    <div className="select">
                        <Select
                            selected={formState.staffCount}
                            list={amountOptionList}
                            placeholder={getPlaceHolder(lang('请选择员工人数'))}
                            onSelect={staffCountChangeCallback}
                        />
                    </div>
                </div>
            </If>
            <If
                condition={
                    ![
                        FormType.whitePaper,
                        FormType.practiceMakesPerformance,
                        FormType.aiEmpowermentOrganizationalTransformation,
                        FormType.aiFutureOfWork,
                        FormType.talentTransformationAgeOfAi,
                    ].includes(props.type)
                }
            >
                <div className="form-row comment-wrapper">
                    <If condition={props.platform === 'twww'}>
                        <div className="lable-title">{isEn ? 'Message' : lang('留言')}</div>
                    </If>
                    <div className="input">
                        <Textarea
                            value={formState.description}
                            placeholder={
                                isEn
                                    ? getPlaceHolderByPlatform('Optional', 'Message (optional)')
                                    : props.platform === 'twww'
                                    ? lang('请填写留言')
                                    : lang('请填写留言(选填)')
                            }
                            onChange={descriptionChangeCallback}
                            maxLength={_lang('cn') || _lang('jp') ? 1000 : 2000}
                        />
                    </div>
                </div>
            </If>
            <If condition={props.type === FormType.contractSales && (isCom() || isIo())}>
                <div className="form-row receive-subscribe-row">
                    <Checkbox checked={formState.receiveSubscribe} onChange={(e) => receiveSubscribeChangeCallback(e)}>
                        {lang('接收来自UMU的订阅材料')}
                    </Checkbox>
                </div>
            </If>
            <div className={`contract-sales-footer ${props.platform} ${props.type}`}>{renderButton()}</div>
        </>
    );
}

export default Form;
